import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { HttpClient } from "@angular/common/http";
import {
  AvatarUsage, CreateDataSource, CreateRolePlayQuestionAnswerRequest, CreateRolePlayCoachRequest,
  RolePlayEndRequest,
  RoleplayCardInfo,
  UpdateTrainerFeedback
} from '../model/rolePlay';
import {
  CONVERSATION_SERVICE,
  DOCUMENTS_SERVICE,
  ROLEPLAY_SERVICE,
  AVATAR_SERVICE,
  ADMIN_SERVICE
} from "./service-url";
import { KeyVaultService } from './key-vault.service';
import { skipSpinnerOptions } from '../v2/core/services/http-options.utils';


@Injectable({
  providedIn: 'root'
})
export class RoleplayApiService {
  apimUrl:string = '';
  private RolePlayDataSource = new BehaviorSubject([]);
  sharedData = this.RolePlayDataSource.asObservable();

  private stepperSubject = new BehaviorSubject({ current: 1, type: 'N' }); // N - Next, B - Back
  stepperData = this.stepperSubject.asObservable();

  private visibilitySubject = new BehaviorSubject<boolean>(false);
  visibility$ = this.visibilitySubject.asObservable();

  private setRoleplayInfoSubject = new BehaviorSubject({} as RoleplayCardInfo);
  setRoleplayInfoSidebar$ = this.setRoleplayInfoSubject.asObservable();

  private setRolePlayInforClicked = new Subject();
  setRolePlayInforClicked$ = this.setRolePlayInforClicked.asObservable();

  private feedbackValueSubject = new BehaviorSubject({});
  setFeedbackValue$ = this.feedbackValueSubject.asObservable();

  private submitFeedbackSubject = new Subject();
  submitFeedbackI$ = this.submitFeedbackSubject.asObservable();

  private visibilitySubjectSecond = new BehaviorSubject<boolean>(true);
  visibilitySecond$ = this.visibilitySubjectSecond.asObservable();

  constructor(private http: HttpClient, private readonly keyVaultService: KeyVaultService) { }

  setVisibility(isVisible: boolean): void {
    this.visibilitySubject.next(isVisible);
  }
  changeData(rolePlayData: any[] = []) {
    this.RolePlayDataSource.next(rolePlayData);
  }
  changeStepper(data: any = {}) {
    this.stepperSubject.next(data);
  }
  setRoleplayInfoSidebar(data: any) {
    this.setRoleplayInfoSubject.next(data);
  }
  setRoleplayClicked(data: any) {
    this.setRolePlayInforClicked.next(data);
  }

  setFeedbackValue(data: any) {
    this.feedbackValueSubject.next(data);
  }
  submitFeedbackHandler(data: any) {
    this.submitFeedbackSubject.next(data);
  }

  public getQuestionAnswer(request: CreateRolePlayQuestionAnswerRequest): Observable<any> {
    if (request) {
      return this.http.post(ROLEPLAY_SERVICE.roleplayLlmResponse, request);
    }
    else {
      return null;
    }
  }

  public getDoctorResponse(request: CreateRolePlayQuestionAnswerRequest): Observable<any> {
    if (request) {
      return this.http.post(ROLEPLAY_SERVICE.roleplayDoctorResponse, request, skipSpinnerOptions());
    } else {
      return null;
    }
  }

  public getCoachResponse(request: CreateRolePlayCoachRequest): Observable<any> {
    if (request) {
      return this.http.post(ROLEPLAY_SERVICE.roleplayCoachResponse, request, skipSpinnerOptions());
    } else {
      return null;
    }
  }


  public endRolePlayRequest(request: RolePlayEndRequest): Observable<any> {
    if (request) {
      return this.http.post(ROLEPLAY_SERVICE.getRoleplayEndSessionFeedback, request);
    }
    else {
      return null;
    }
  }
  public stopAvatar(request: AvatarUsage): Observable<any> {
    if (request) {
      return this.http.post(AVATAR_SERVICE.stopAvatarConversation, request)
    } else {
      return null;
    }
  }

  hideDiv(value) {
    this.visibilitySubjectSecond.next(value);
  }

  public getUserScenarioDetails(scenarioId: number, wwId: string): Observable<any> {
    return this.http.get<any[]>(`${ROLEPLAY_SERVICE.getUserAvatarDetailsByUserScenario}/${scenarioId}/${wwId}`);
  }

  public getChatHistory(id: number): Observable<any> {
    return this.http.get<any[]>(`${CONVERSATION_SERVICE.getRoleplayChatHistoryBySessionId}/${id}`);
  }

  public getTrainerFeedback(id: number): Observable<any> {
    return this.http.get<any[]>(`${ROLEPLAY_SERVICE.getTrainerFeedbackBySessionId}/${id}`);
  }

  public updateTrainerFeedback(id: number, request: UpdateTrainerFeedback): Observable<any> {
    if (request) {
      return this.http.put(`${ROLEPLAY_SERVICE.updateTrainerFeedback}/${id}`, request);
    }
    else {
      return null;
    }
  }

  getDoctorProfileList(business_unit_code): Observable<any> {
    return this.http.get(ROLEPLAY_SERVICE.doctorProfileList + business_unit_code);
  }
  getDataSourceList(business_unit_code, region_name): Observable<any> {
    return this.http.get(ADMIN_SERVICE.getDataSorceList + '/' + business_unit_code + `?region=${region_name}`);
  }
  getAMPDataSourceList(business_unit_code, region_name): Observable<any> { 
    return this.http.get(DOCUMENTS_SERVICE.getAMPDataSorceList + '/' + business_unit_code + `?primary_region=${region_name}`);
  }
  getAMPDataSourceListBySourceFile(sourceFile): Observable<any> {
    return this.http.get(DOCUMENTS_SERVICE.getAMPDataSorceListBySourceName + '/' + sourceFile);
  }
  submitFileData(request: CreateDataSource): Observable<any> {
    if (request) {
      return this.http.post(DOCUMENTS_SERVICE.createDataPath, request)
    } else {
      return null;
    }
  }
  getDetailDataSourceList(id: number): Observable<any> {
    return this.http.get(DOCUMENTS_SERVICE.getDetailDataSource + '/' + id)
  }
  getJobList(): Observable<any> {
    return this.http.get(ADMIN_SERVICE.getJobSchedulerList);
  }
  getJobMetaData(): Observable<any> {
    return this.http.get(DOCUMENTS_SERVICE.getVeevaVaultMetadata);
  }

  /** Get roleplay history by session id */
  public chatRoleplayHistoryList(sessionId: number): Observable<any> {
    if (sessionId > 0)
      return this.http.get(ROLEPLAY_SERVICE.roleplayHistoryBySessionId + '/' + sessionId);
    else
      return null;
  }

  public getRoleplayFeedbackSessionId(SessionId: string | number): Observable<any> {
    return this.http.get<any[]>(`${ROLEPLAY_SERVICE.getRoleplayFeedbackBySessionId}/${SessionId}`);
  }

  public getRoleplayEndSessionFeedback(payload): Observable<any> {
    return this.http.post<any[]>(`${ROLEPLAY_SERVICE.getRoleplayEndSessionFeedback}`,payload);
  }

  intializeAPIUrl() {
    this.keyVaultService.apimUrl$.pipe(take(1)).subscribe(url => this.apimUrl = url);
  }
}

